import { IFedexV2MfaOptions, IFedexV2RegistrationAddress } from '../../../../../../../models/IntegrationsTypes';
import ShippingIntegrationsService from '../../../../../../../services/api/shipping-integrarions-service';
import { TYPE, rootContainer } from '../../../../../../../services/container/root';

export type FedexV2ChildCredentials = {
    childKey: string;
    childSecret: string;
};

export type FedexV2ValidateAddressResult =
    | { type: 'credentials'; credentials: FedexV2ChildCredentials }
    | { type: 'mfa'; mfaOption: IFedexV2MfaOptions };

export type FedexV2ValidateAddressInput = {
    accountNumber: string;
    customerName: string;
    address: IFedexV2RegistrationAddress;
    isSandbox: boolean;
};

class FedexV2RegistrationApi {
    constructor(private readonly isSandbox: boolean) {}

    private get service(): ShippingIntegrationsService {
        return rootContainer.get<ShippingIntegrationsService>(TYPE.ShippingIntegrationsService);
    }

    async validateAddress(input: Omit<FedexV2ValidateAddressInput, 'isSandbox'>): Promise<FedexV2ValidateAddressResult> {
        const response = await this.service.fedexV2ValidateAddress({
            accountNumber: input.accountNumber,
            customerName: input.customerName,
            address: input.address,
            isSandbox: this.isSandbox,
        });

        const output = response.output;
        const addressCredentials = output?.credentials;
        if (addressCredentials?.child_Key && addressCredentials?.child_secret) {
            return {
                type: 'credentials',
                credentials: {
                    childKey: addressCredentials.child_Key,
                    childSecret: addressCredentials.child_secret,
                },
            };
        }

        const mfaOption = output?.mfaOptions?.[0];
        if (!mfaOption?.accountAuthToken) {
            throw new Error('FedEx did not return MFA options for this account');
        }

        return { type: 'mfa', mfaOption };
    }

    async generatePin(accountAuthToken: string, option: string): Promise<void> {
        await this.service.fedexV2GeneratePin({
            accountAuthToken,
            option,
            isSandbox: this.isSandbox,
        });
    }

    async validatePin(accountAuthToken: string, secureCodePin: string): Promise<FedexV2ChildCredentials> {
        const response = await this.service.fedexV2ValidatePin({
            accountAuthToken,
            secureCodePin,
            isSandbox: this.isSandbox,
        });

        const output = response.output;
        if (!output?.child_Key || !output?.child_secret) {
            throw new Error('FedEx account registration failed');
        }

        return {
            childKey: output.child_Key,
            childSecret: output.child_secret,
        };
    }
}

export default FedexV2RegistrationApi;
